import { useState } from 'react'
import { ShieldAlert, ChevronDown, ChevronRight } from 'lucide-react'
import { decideAccess } from '@petrotrack/shared'
import { Card } from '../ui'

// Dry run for enforcing subscription state in the database.
//
// Today the lock lives only in the clients, so a locked org's tokens still
// work against the REST API. Before moving that check into RLS we need to know
// exactly who it would cut off — a policy mistake locks paying customers out
// of their own data, and unlike a UI bug it cannot be rolled back from Vercel.
//
// This applies the same decideAccess() rule the clients use to every org, and
// reports who would lose API access if that rule became a policy. It changes
// nothing.
const REASON_LABELS = {
  suspended: 'Manually suspended',
  subscription_lapsed: 'Subscription lapsed',
  trial_expired: 'Trial expired',
}

export default function AccessBlastRadius({ organizations, users }) {
  const [open, setOpen] = useState(false)

  const rows = organizations.map((org) => {
    // Evaluated as a plain member: superadmins bypass the rule, so asking
    // "would an admin in this org be locked?" is the question that matters.
    const access = decideAccess({
      role: 'admin',
      subscriptionStatus: org.subscriptionStatus,
      trialEndsAt: org.trialEndsAt,
      suspendedAt: org.suspendedAt,
      graceUntil: org.graceUntil,
    })
    const members = users.filter((u) => u.organizationId === org.id && u.role !== 'superadmin')
    return { org, access, members: members.length, active: members.filter((u) => u.isActive !== false).length }
  })

  const blocked = rows.filter((r) => r.access.locked)
  const inGrace = rows.filter((r) => !r.access.locked && r.access.reason === 'in_grace')
  const blockedUsers = blocked.reduce((n, r) => n + r.active, 0)
  const paying = blocked.filter((r) => r.org.subscriptionStatus === 'active').length

  const byReason = blocked.reduce((acc, r) => {
    acc[r.access.reason] = (acc[r.access.reason] || 0) + 1
    return acc
  }, {})

  return (
    <Card title="RLS enforcement — blast radius" hint="dry run, changes nothing">
      <div className="px-4 py-3.5 space-y-3">
        <p className="text-xs text-gray-500">
          Subscription state is not enforced in the database yet, so a locked organisation's
          tokens still work against the API. If the current lock rule became an RLS policy today,
          this is who would lose access to their data.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Orgs blocked" value={blocked.length} tone={blocked.length ? 'danger' : 'ok'} />
          <Stat label="Active users cut off" value={blockedUsers} tone={blockedUsers ? 'danger' : 'ok'} />
          <Stat label="In payment grace" value={inGrace.length} tone={inGrace.length ? 'warn' : 'ok'} />
          <Stat label="Paying but blocked" value={paying} tone={paying ? 'danger' : 'ok'} />
        </div>

        {paying > 0 && (
          <p className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5 flex-none mt-0.5" />
            {paying} organisation{paying !== 1 ? 's are' : ' is'} paying but would still be blocked —
            investigate before enforcing anything.
          </p>
        )}

        {blocked.length > 0 && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(byReason).map(([reason, n]) => (
                <span key={reason} className="text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">
                  {REASON_LABELS[reason] || reason}: {n}
                </span>
              ))}
            </div>

            <button onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition">
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {open ? 'Hide' : 'Show'} the {blocked.length} affected organisation{blocked.length !== 1 ? 's' : ''}
            </button>

            {open && (
              <div className="border border-gray-100 rounded-xl overflow-x-auto">
                <table className="w-full min-w-[460px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="text-left px-3 py-2">Organisation</th>
                      <th className="text-left px-3 py-2">Why</th>
                      <th className="text-right px-3 py-2">Active users</th>
                      <th className="text-left px-3 py-2">Billing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blocked.map(({ org, access, active }) => (
                      <tr key={org.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-3 py-2 text-[13px] font-semibold text-gray-800">{org.name}</td>
                        <td className="px-3 py-2 text-[12.5px] text-gray-600">{REASON_LABELS[access.reason] || access.reason}</td>
                        <td className="px-3 py-2 text-right text-[13px] tabular-nums text-gray-700">{active}</td>
                        <td className="px-3 py-2 text-[12.5px] text-gray-500 capitalize">{org.subscriptionStatus}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {blocked.length === 0 && (
          <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            No organisation would be blocked right now — enforcing this in RLS would be a no-op today.
          </p>
        )}
      </div>
    </Card>
  )
}

function Stat({ label, value, tone }) {
  const cls = {
    danger: 'text-red-700', warn: 'text-amber-700', ok: 'text-gray-800',
  }[tone] || 'text-gray-800'
  return (
    <div className="bg-gray-50 rounded-xl px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`text-xl font-bold tabular-nums mt-0.5 ${cls}`}>{value}</p>
    </div>
  )
}
